import { SettingsIcon } from '@/assets/icons'
import InviteModal from '@components/InviteModal'
import PageLoader from '@components/PageLoader'
import { Hero, TripOverview, TripOverviewSection } from '@components/TripOverview'
import { buildTripOverviewData, sectionPath } from '@components/TripOverview/core'
import { PageColumn, PageScroll } from '@components/Page'
import { useBreadcrumbs } from '@context/BreadcrumbContext'
import {
	useAccommodations,
	useEvents,
	useExpenses,
	useFiles,
	useFlights,
	useIdeas,
	useMe,
	usePayUps,
	useTravelSegments,
	useTrip,
	useTripMembers,
} from '@hooks'
import { isInlineImageMime } from '@lib/mime'
import { sortGroupsByDeparture } from '@pages/Flights/flightHelpers'
import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import EditTripModal from '../EditTripModal'
import { editTripDestinationsPath } from '../EditTripDestinationsFlow/paths'
import { TripSettingsButton } from './TripDetailPage.styles'
import { iconSizes } from '@/theme/tokens'
import { ErrorNote } from '@components/Notice'

const TripDetailPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const navigate = useNavigate()
	const { data: trip, isLoading: tripLoading } = useTrip(tripId)
	const { data: me } = useMe()
	const { data: eventsData, isLoading: eventsLoading } = useEvents(tripId)
	// Segments enhance the itinerary preview when they land — they never gate it.
	const { data: travelSegmentsData } = useTravelSegments(tripId)
	const { data: ideasData, isLoading: ideasLoading } = useIdeas(tripId)
	const { data: membersData } = useTripMembers(tripId)
	const { data: accommodationsData, isLoading: accommodationsLoading } = useAccommodations(tripId)
	const { data: flightsData, isLoading: flightsLoading } = useFlights(tripId)
	const { data: filesData, isLoading: filesLoading } = useFiles(tripId)
	const { data: expensesData, isLoading: expensesLoading } = useExpenses(tripId)
	const { data: payUpsData, isLoading: payUpsLoading } = usePayUps(tripId)
	const [isEditTripModalOpen, setIsEditTripModalOpen] = useState(false)
	const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)

	// The trip overview is the trail's ROOT (every other trip page starts its
	// breadcrumbs at the trip name) — it renders no trail of its own.
	useBreadcrumbs([])

	if (tripLoading) {
		return (
			<PageScroll>
				<PageLoader label="Loading trip details…" />
			</PageScroll>
		)
	}

	if (!trip) {
		return (
			<PageScroll>
				<PageColumn>
					<ErrorNote>Trip not found</ErrorNote>
				</PageColumn>
			</PageScroll>
		)
	}

	// Unwrap the hook responses into the pure normalizer's inputs. Joins that need
	// two queries (accommodation photo ← files) happen here, not in the core.
	const files = filesData?.files ?? []
	const filesById = new Map(files.map(file => [file.id, file]))

	// The stay's first attachment, matching the public overview. A non-image
	// fails to load and Thumbnail falls back to its icon chip.
	const stayPhotoUrl = (fileIds: readonly string[]): string | null =>
		(fileIds[0] && filesById.get(fileIds[0])?.url) || null

	const firstLegs = sortGroupsByDeparture(flightsData?.flightGroups ?? [])
		.map(group => group.flights[0])
		.filter(leg => leg != null)

	const overviewData = buildTripOverviewData({
		trip: { name: trip.name, startDate: trip.startDate ?? null, dayCount: trip.dayCount },
		members: membersData?.members,
		events: eventsData?.events,
		travelSegments: travelSegmentsData?.segments,
		ideas: ideasData?.ideas?.map(idea => ({
			id: idea.id,
			name: idea.name,
			thumbnailUrl: idea.links.find(link => link.thumbnail)?.thumbnail ?? null,
		})),
		accommodations: accommodationsData?.accommodations?.map(stay => ({
			id: stay.id,
			name: stay.name,
			city: stay.location?.locality || stay.location?.secondaryText || null,
			photoUrl: stayPhotoUrl(stay.fileIds),
			checkInDate: stay.checkInDate,
			checkOutDate: stay.checkOutDate,
		})),
		flights: firstLegs.map(leg => ({
			id: leg.id,
			airlineIata: leg.airlineIata,
			flightNumber: leg.flightNumber,
			originCode: leg.departureAirportCode ?? leg.departureAirport?.iataCode ?? null,
			destinationCode: leg.arrivalAirportCode ?? leg.arrivalAirport?.iataCode ?? null,
			departureTime: leg.departureTime,
			departureTimeStr: leg.departureTimeStr,
			durationMinutes: leg.durationMinutes,
		})),
		files: files.map(file => ({
			id: file.id,
			fileName: file.fileName,
			url: file.url,
			thumbnailUrl: isInlineImageMime(file.mimeType) ? file.url : null,
			mimeType: file.mimeType,
		})),
		money: {
			defaultCurrencyCode: trip.defaultCurrencyCode,
			expenses: expensesData?.expenses,
			payUps: payUpsData?.payUps,
		},
	})

	// Per-tile skeletons while each tile's own queries load — no page spinner.
	const tileLoading = {
		itinerary: eventsLoading,
		ideas: ideasLoading,
		accommodations: accommodationsLoading,
		flights: flightsLoading,
		files: filesLoading,
		money: expensesLoading || payUpsLoading,
	}

	const handleNavigate = (section: TripOverviewSection) => {
		navigate(sectionPath(tripId ?? '', section))
	}

	// The destinations editor comes back here, not to the itinerary, when
	// opened from the overview.
	const openDestinationsEditor = () => {
		if (!tripId) return
		navigate(editTripDestinationsPath(tripId), { state: { returnTo: `/trips/${tripId}` } })
	}

	return (
		<PageScroll>
			<PageColumn>
				<Hero
					hero={overviewData.hero}
					onAvatarsClick={() => navigate(`/trips/${tripId}/members`)}
					onInviteClick={() => setIsInviteModalOpen(true)}
					action={
						<TripSettingsButton onClick={() => setIsEditTripModalOpen(true)} aria-label="Trip settings">
							<SettingsIcon sx={{ fontSize: iconSizes.md }} />
							<span>Trip Settings</span>
						</TripSettingsButton>
					}
				/>

				<TripOverview
					data={overviewData}
					mode="member"
					loading={tileLoading}
					onNavigate={handleNavigate}
					onSetTripDates={openDestinationsEditor}
				/>
			</PageColumn>

			<EditTripModal
				isOpen={isEditTripModalOpen}
				onClose={() => setIsEditTripModalOpen(false)}
				tripId={tripId || ''}
				tripCode={trip.code}
				initialName={trip.name}
				initialCurrencyCode={trip.defaultCurrencyCode}
				isOwner={me ? me.id === trip.ownerUserId : undefined}
			/>

			<InviteModal
				isOpen={isInviteModalOpen}
				onClose={() => setIsInviteModalOpen(false)}
				tripCode={trip.code}
			/>
		</PageScroll>
	)
}

export default TripDetailPage
